import { useState, type FormEvent } from "react";

import { useAppDispatch } from "../app/hooks";
import { setTokens } from "../features/auth/authSlice";
import { useToast } from "../hooks/useToast";
import { useChangePasswordMutation } from "../services/api";
import { logFrontendError } from "../services/errorReporter";
import { logEvent } from "../services/logger";
import { RequiredMark } from "./RequiredMark";

const MIN_LENGTH = 8;

/**
 * Change your own password (24-Sep-2026: there was no way to do this at all).
 *
 * The server checks the current password, signs out every other device and
 * hands back fresh tokens, which are stored here so THIS device carries on.
 */
interface ChangePasswordCardProps {
    /** Heading; defaults to the Profile screen's wording. */
    title?: string;
    intro?: string;
    /** The labels the first-sign-in screen uses ("Temporary password"). */
    currentLabel?: string;
    submitLabel?: string;
    /** Render without its own card, inside a host that already is one. */
    bare?: boolean;
    onChanged?: () => void;
}

export const ChangePasswordCard = ({
    title = "Change password",
    intro = "After the change you stay signed in here, and every other device is signed out.",
    currentLabel = "Current password",
    submitLabel = "Change password",
    bare = false,
    onChanged
}: ChangePasswordCardProps = {}) => {
    const dispatch = useAppDispatch();
    const { showSuccess } = useToast();
    const [changePassword, { isLoading }] = useChangePasswordMutation();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string; server?: string }>({});

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        const found: typeof errors = {};
        if (!current) found.current = `Enter your ${currentLabel.toLowerCase()}.`;
        if (next.length < MIN_LENGTH) found.next = `Use at least ${MIN_LENGTH} characters.`;
        else if (next === current) found.next = "Choose a password different from your current one.";
        if (confirm !== next) found.confirm = "The two new passwords do not match.";
        setErrors(found);
        if (Object.keys(found).length) return;
        try {
            const tokens = await changePassword({ current_password: current, new_password: next }).unwrap();
            dispatch(setTokens({ access: tokens.access_token, refresh: tokens.refresh_token }));
            setCurrent("");
            setNext("");
            setConfirm("");
            logEvent({ level: "INFO", component: "ChangePasswordCard", message: "auth.password_changed" });
            showSuccess("Password changed. You have been signed out on every other device.");
            onChanged?.();
        } catch (error) {
            const detail = (error as { data?: { detail?: string } })?.data?.detail;
            setErrors({ server: typeof detail === "string" ? detail : "Could not change the password. Try again." });
            await logFrontendError("ChangePasswordCard.submit", "Password change failed", error);
        }
    };

    const heading = (
        <header className={bare ? "change-password__header" : "card__header"}>
            <div>
                <h3>{title}</h3>
                <p className="form-hint">{intro}</p>
            </div>
        </header>
    );
    const form = (
            <form className={bare ? "auth-form" : "form-grid"} onSubmit={submit} noValidate>
                <label className={bare ? "form-field" : undefined}>
                    <Caption bare={bare}>{currentLabel}</Caption>
                    <input
                        className="text-input"
                        type="password"
                        autoComplete="current-password"
                        value={current}
                        aria-invalid={Boolean(errors.current)}
                        onChange={(e) => setCurrent(e.target.value)}
                    />
                    {errors.current && <p className="field-error" role="alert">{errors.current}</p>}
                </label>
                <label className={bare ? "form-field" : undefined}>
                    <Caption bare={bare}>New password</Caption>
                    <input
                        className="text-input"
                        type="password"
                        autoComplete="new-password"
                        value={next}
                        aria-invalid={Boolean(errors.next)}
                        onChange={(e) => setNext(e.target.value)}
                    />
                    {errors.next ? (
                        <p className="field-error" role="alert">{errors.next}</p>
                    ) : (
                        <small className="form-hint">At least {MIN_LENGTH} characters.</small>
                    )}
                </label>
                <label className={bare ? "form-field" : undefined}>
                    <Caption bare={bare}>Confirm new password</Caption>
                    <input
                        className="text-input"
                        type="password"
                        autoComplete="new-password"
                        value={confirm}
                        aria-invalid={Boolean(errors.confirm)}
                        onChange={(e) => setConfirm(e.target.value)}
                    />
                    {errors.confirm && <p className="field-error" role="alert">{errors.confirm}</p>}
                </label>
                {errors.server && <p className="field-error" role="alert">{errors.server}</p>}
                <div>
                    <button className="btn btn-primary" type="submit" disabled={isLoading}>
                        {isLoading ? "Changing…" : submitLabel}
                    </button>
                </div>
            </form>
    );
    if (bare) {
        return (
            <>
                {heading}
                {form}
            </>
        );
    }
    return (
        <section className="card">
            {heading}
            {form}
        </section>
    );
};

/** On a sign-in style page each label is a column, so the name and its
 *  required mark must be ONE row (the shared .form-label); on the Profile card
 *  they stay inline text as they always were. */
const Caption = ({ bare, children }: { bare: boolean; children: string }) =>
    bare ? (
        <span className="form-label">
            {children} <RequiredMark />
        </span>
    ) : (
        <>
            {children} <RequiredMark />
        </>
    );
